/**
 * Spec 218 PR3 — Invoice list (`/invoices`).
 *
 * Read-only table over `listInvoices(ctx)`. Filters:
 *   - Year selector (derived from the loaded invoices)
 *   - Status toggle group (all / accepted / pending / rejected)
 *   - Recipient name substring search
 *
 * Fetch happens at mount + on retry. The list is not auto-refreshed
 * via subscribeStore because invoices land on a different code path
 * than works (HTTP only, no IDB cache today). A manual reload
 * picks up new emissions.
 *
 * Empty + error states reuse `EmptyState` for consistency with the
 * other routes.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import * as ToggleGroup from '@radix-ui/react-toggle-group';
import { ChevronLeft, ChevronRight, FileText, Plus, RefreshCw, Search, X } from 'lucide-react';

import { EmptyState } from '../components/EmptyState';
import { SkeletonList } from '../components/Skeleton';
import {
  listInvoices,
  type InvoiceSummary,
  type InvoicingCtx,
} from '../sync/invoicing';
import type { WebIdentity } from '../store/keys';

import './InvoiceList.css';

interface InvoiceListProps {
  identity: WebIdentity;
}

type StatusFilter = 'all' | 'accepted' | 'pending' | 'rejected';

const STATUS_OPTIONS: ReadonlyArray<{ key: StatusFilter; label: string }> = [
  { key: 'all', label: 'Todas' },
  { key: 'accepted', label: 'Aceptadas' },
  { key: 'pending', label: 'Pendientes' },
  { key: 'rejected', label: 'Rechazadas' },
];

const STATUS_LABEL: Record<string, string> = {
  accepted: 'Aceptada',
  pending: 'Pendiente',
  rejected: 'Rechazada',
  failed: 'Error',
  cancelled: 'Anulada',
};

function buildCtx(identity: WebIdentity): InvoicingCtx {
  return { companyToken: identity.companyToken };
}

function formatTotal(amount: number): string {
  // Relay returns in_total as euros (NUMERIC(14,2)). Earlier code
  // divided by 100 here, producing "1,21 €" for a 121 € invoice. See
  // spec 218 PR5 for the full fix; the divide-by-100 was a pre-existing
  // PR3 bug surfaced by the QA review.
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return d.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function yearOf(iso: string): number {
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.getUTCFullYear() : 0;
}

export function InvoiceList({ identity }: InvoiceListProps) {
  const [invoices, setInvoices] = useState<InvoiceSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [year, setYear] = useState<number | 'all'>('all');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setInvoices(null);
    setLoadError(null);
    void listInvoices(buildCtx(identity)).then(
      (rows) => {
        if (cancelled) return;
        setInvoices(rows);
      },
      (err) => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : String(err));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [identity, reloadKey]);

  const years = useMemo(() => {
    if (!invoices) return [] as number[];
    const set = new Set<number>();
    for (const inv of invoices) {
      const y = yearOf(inv.issueDate);
      if (y) set.add(y);
    }
    return Array.from(set).sort((a, b) => b - a);
  }, [invoices]);

  const filtered = useMemo(() => {
    if (!invoices) return [] as InvoiceSummary[];
    let out = invoices;
    if (year !== 'all') {
      out = out.filter((inv) => yearOf(inv.issueDate) === year);
    }
    if (status !== 'all') {
      out = out.filter((inv) =>
        status === 'accepted'
          ? inv.aeatStatus === 'accepted' && !inv.cancelled
          : inv.aeatStatus === status,
      );
    }
    const q = search.trim().toLocaleLowerCase('es');
    if (q) {
      out = out.filter(
        (inv) =>
          inv.recipientName?.toLocaleLowerCase('es').includes(q) ||
          inv.recipientNif?.toLocaleLowerCase('es').includes(q) ||
          inv.number.includes(q) ||
          inv.series.toLocaleLowerCase('es').includes(q),
      );
    }
    // Newest issuance first.
    return [...out].sort((a, b) => (a.issueDate < b.issueDate ? 1 : -1));
  }, [invoices, year, status, search]);

  return (
    <main id="main-content" className="il-screen" data-testid="invoice-list-screen">
      <header className="il-header">
        <Link to="/" className="il-back" aria-label="Volver" data-testid="invoice-list-back">
          <ChevronLeft size={20} strokeWidth={2} aria-hidden="true" />
          Volver
        </Link>
        <div className="il-header-title">
          <FileText size={24} strokeWidth={2} aria-hidden="true" />
          <h1 className="il-h1">Facturas</h1>
        </div>
        <p className="il-header-subtitle">
          {invoices === null
            ? ' '
            : `${invoices.length} ${invoices.length === 1 ? 'factura' : 'facturas'} en total · ${filtered.length} visibles con los filtros actuales.`}
        </p>
        <Link to="/invoices/new" className="il-cta-new" data-testid="invoice-list-new">
          <Plus size={18} strokeWidth={2} aria-hidden="true" />
          <span>Nueva factura</span>
        </Link>
      </header>

      <div className="il-toolbar">
        <div className="il-search">
          <Search size={20} strokeWidth={2} className="il-search-icon" aria-hidden="true" />
          <input
            type="search"
            placeholder="Cliente, NIF o nº factura"
            className="il-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="invoice-list-search"
            aria-label="Buscar factura"
          />
          {search && (
            <button
              type="button"
              className="il-search-clear"
              aria-label="Limpiar búsqueda"
              onClick={() => setSearch('')}
              data-testid="invoice-list-search-clear"
            >
              <X size={16} strokeWidth={2} aria-hidden="true" />
            </button>
          )}
        </div>

        <ToggleGroup.Root
          className="il-status-filter"
          type="single"
          value={status}
          onValueChange={(v) => setStatus((v as StatusFilter) || 'all')}
          aria-label="Filtros de estado AEAT"
        >
          {STATUS_OPTIONS.map((opt) => (
            <ToggleGroup.Item
              key={opt.key}
              value={opt.key}
              className="il-chip"
              data-testid={`invoice-list-status-${opt.key}`}
            >
              {opt.label}
            </ToggleGroup.Item>
          ))}
        </ToggleGroup.Root>

        <label className="il-year">
          <span className="il-year-label">Año:</span>
          <select
            className="il-year-select"
            value={year}
            onChange={(e) => setYear(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            data-testid="invoice-list-year"
          >
            <option value="all">Todos</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          className="il-reload"
          onClick={() => setReloadKey((k) => k + 1)}
          aria-label="Recargar"
          data-testid="invoice-list-reload"
        >
          <RefreshCw size={16} strokeWidth={2} aria-hidden="true" />
        </button>
      </div>

      {invoices === null && !loadError ? (
        <SkeletonList count={5} testId="invoice-list-loading" />
      ) : loadError ? (
        <EmptyState
          testId="invoice-list-error"
          variant="noResults"
          title="No se pudo cargar"
          body={<>{loadError}. Vuelve a intentarlo más tarde.</>}
          action={{
            label: 'Reintentar',
            onClick: () => setReloadKey((k) => k + 1),
            testId: 'invoice-list-error-retry',
          }}
        />
      ) : filtered.length === 0 ? (
        invoices && invoices.length === 0 ? (
          <EmptyState
            testId="invoice-list-empty"
            variant="noResults"
            title="Aún no hay facturas"
            body={<>Cuando emitas tu primera factura desde la app móvil aparecerá aquí.</>}
          />
        ) : (
          <EmptyState
            testId="invoice-list-empty"
            variant="noResults"
            title="Sin resultados"
            body={<>Ninguna factura coincide con los filtros actuales.</>}
          />
        )
      ) : (
        <ul className="il-list" data-testid="invoice-list">
          {filtered.map((inv) => (
            <li key={inv.id}>
              <Link
                to={`/invoices/${inv.id}`}
                className="il-row"
                data-testid={`invoice-list-row-${inv.id}`}
              >
                <div className="il-row-main">
                  <span className="il-row-number">
                    {inv.series}-{inv.number}
                  </span>
                  <span className="il-row-recipient">
                    {inv.recipientName ?? 'Sin destinatario'}
                  </span>
                  <span className="il-row-meta">
                    {formatDate(inv.issueDate)}
                    <span className="il-row-sep">·</span>
                    {STATUS_LABEL[inv.cancelled ? 'cancelled' : inv.aeatStatus] ?? inv.aeatStatus}
                  </span>
                </div>
                <span className="il-row-total" data-testid={`invoice-list-row-${inv.id}-total`}>
                  {formatTotal(inv.total)}
                </span>
                <ChevronRight size={20} strokeWidth={2} aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
